import { useEffect, useState } from 'react'
import { match, P } from 'ts-pattern'
import type { JsonValue } from 'type-fest'
import * as v from 'valibot'
import { DecorationArtworkFrameDataSchema } from 'src/features/UserDecoration/schema/decoration'
import type { UserBaseFragment } from 'src/services/apollo/generated/graphql'
import { DecorationType } from 'src/services/apollo/generated/graphql'
import type { DecorationItem } from 'src/services/orpc/decoration'
import { useUserActiveDecoration } from 'src/services/orpc/decoration'
import { cn, cx } from 'src/utils/cx'
import { userHasMembership } from 'src/utils/user'

export type ArtworkFrameProps = StyleProps & {
  decoration?: DecorationItem
  fillClassName?: string
}

export default function ArtworkFrame(props: ArtworkFrameProps) {
  const decorationData = props.decoration?.data

  return (
    // p-0.5 for matching with actual style of Artwork Card
    <div className="isolate relative size-full p-0.5">
      <div
        className={cn(
          'z-[-1] absolute',
          'transition ease-out duration-300 group-hover:shadow-xl',

          // Normal-sized border
          '-inset-0.5 rounded-[0.625rem]',

          props.className,
        )}
        style={{ ...getArtworkFrameStyle(decorationData) }}
      />
      <div
        className={cn('w-full h-full rounded-[7px]', props.fillClassName)}
      ></div>
    </div>
  )
}

export function getArtworkFrameStyle(decorationData?: JsonValue) {
  const data = v.safeParse(DecorationArtworkFrameDataSchema, decorationData)
  if (!data.success) return {}
  const linearGradient = data.output?.linearGradient
  if (linearGradient) {
    const { degree, colorStops } = linearGradient ?? {}
    const strings = [
      `${degree}deg`,
      ...colorStops.map(stop => `#${stop.colorValue} ${stop.position}%`),
    ]
    return {
      backgroundImage: `linear-gradient(${strings.join(',')})`,
    }
  }
  return {}
}

export function ArtworkFrameForArtworkCard(props: {
  user?: Pick<UserBaseFragment, 'activeDecorations' | 'membership'> & {
    activeDecorationIds?: string[]
  }
  decorations?: {
    /**
     * true: follow user's active frame
     * Any Decoration: will take precedence
     */
    frame: boolean | DecorationItem
  }
  borderClassName?: string
}) {
  const { user, decorations, borderClassName } = props
  const userActiveArtworkFrameDecoration = useUserActiveDecoration(
    user,
    DecorationType.ArtworkFrame,
  )
  // SSR-safe deopt: always render the default (plain) frame on the
  // server pass and the very first client commit, then upgrade to the
  // membership / decoration frame after the `useEffect` below flips
  // `hydrated` to `true`.
  //
  // Why: the membership / decoration branches read `user.membership`
  // and `user.activeDecorationIds` (via `useUserActiveDecoration` →
  // react-query worker fetch). The recommend-feed grid on `/` ships
  // a fully-populated `user` from the SSR-prefetched oRPC response,
  // but the client's first hydrate commit can briefly observe an
  // *empty* user (user-id'd `useInfiniteQuery` cache miss between
  // the SSR streaming payload arriving and react-query's structural
  // sharing committing) — which makes SSR render the fancy frame
  // (membership / decoration) and the first hydrate commit render
  // the default frame, on every artwork card. React 19 then treats
  // that as a structural mismatch on every <article> and discards
  // the entire SSR DOM, regenerating the whole grid on the client.
  // The visible symptom on `/<lang>/` is the SEO-rich SSR HTML
  // flashing in for one frame, then being replaced by skeleton
  // cards while the client re-renders from scratch.
  //
  // Forcing the default frame on the first commit makes that diff
  // structurally identical to the SSR HTML on EVERY card (regardless
  // of which user owns the card), so React hydrates cleanly. The
  // post-`useEffect` re-render then upgrades cards whose authors
  // have a real membership / decoration to the fancy frame — that
  // re-render is local to each card subtree and never tears the
  // whole grid out, so users see at most a single soft frame swap
  // for the small subset of cards owned by paid creators (and zero
  // visible change for the majority of plain-author cards).
  //
  // Trade-off: paid-creator decoration / membership frame doesn't
  // reach the SSR HTML, so SEO crawlers / view-source readers see
  // every frame as plain. That's acceptable: the fancy border is
  // pure visual chrome, not indexable content. The artwork's
  // `<img>`, title, author link, and JSON-LD all stay in the SSR
  // tree where SEO actually wants them.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  /**
   * If no active frame enabled, fallback to membership frame
   */
  const decorationFrame = match(decorations)
    .returnType<DecorationItem | undefined>()
    .with({ frame: P.select(P.not(P.boolean)) }, frame => frame)
    .with(
      P.union(P.optional(undefined), { frame: true }),
      () => userActiveArtworkFrameDecoration,
    )
    .otherwise(() => undefined)

  const memberStyleEnabled = userHasMembership(user) && !decorationFrame

  if (hydrated && memberStyleEnabled)
    return (
      <div
        className={cx(
          'z-[-1] absolute',
          'transition ease-out duration-300 group-hover:shadow-xl',

          // Normal-sized border
          '-inset-0.5 rounded-[0.625rem]',
          // Smaller border for mobile view
          'max-mmd:inset-0 max-mmd:rounded-lg',

          // Membership border
          'bg-gradient-member',
          borderClassName,
        )}
      />
    )

  // Ignore decoration artwork frame unless user has feature flag enabled.
  // Same hydration deopt as the membership branch above — only render
  // the decoration frame after the first hydrate commit completes.
  if (hydrated && decorationFrame) {
    const artworkFrameStyle = getArtworkFrameStyle(decorationFrame.data)

    return (
      <div
        className={cx(
          'z-[-1] absolute',
          'transition ease-out duration-300 group-hover:shadow-xl',

          // Normal-sized border
          '-inset-0.5 rounded-[0.625rem]',
          // Smaller border for mobile view
          'max-mmd:inset-0 max-mmd:rounded-lg',

          borderClassName,
        )}
        style={artworkFrameStyle}
      />
    )
  }
  return (
    <div
      className={cx(
        'z-[-1] absolute inset-px rounded-lg',
        'bg-black/10 group-hover:bg-black/30',
        'transition ease-out duration-300 group-hover:shadow-xl',
        borderClassName,
      )}
    />
  )
}
